import { CorporateAccountApi } from '@lib/state/api/generated/reservations';
import { invoiceRunStore, InvoiceRunStore } from './invoice-run.store';
import { defer, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetInvoiceRunRequest, InvoiceRun } from './invoice-run.model';
import { applyTransaction, setLoading } from '@datorama/akita';
import {
  CommonForm,
  dispatchForm,
  MediaTypes,
  PaginatedQuery,
  PaginationState,
  pollUntil,
  unsubscribeCallback,
} from '@lib/state';
import { InvoiceRunForms } from './invoice-run.forms';

export class InvoiceRunService {
  constructor(private api: CorporateAccountApi, private store: InvoiceRunStore) {}

  getInvoiceRuns(
    {
      createdStartDate,
      createdEndDate,
      invoiceStartDate,
      invoiceEndDate,
      status,
      isDraft,
      billingFrequencyType,
      invoiceType,
      sortBy,
    }: GetInvoiceRunRequest,
    { continuationToken, limit }: PaginatedQuery
  ) {
    from(
      this.api.corporateaccountInvoiceRunsGet(
        continuationToken ?? undefined,
        limit ?? 20,
        createdStartDate?.toString(),
        createdEndDate?.toString(),
        invoiceStartDate?.toString(),
        invoiceEndDate?.toString(),
        status,
        isDraft,
        billingFrequencyType,
        invoiceType
      )
    )
      .pipe(
        map(data => data.data),
        setLoading(this.store)
      )
      .subscribe(({ data, ...pagination }) =>
        applyTransaction(() => {
          this.store.upsertMany(data);
          this.updatePaginationState(pagination);
          this.updateUI({
            createdStartDate,
            createdEndDate,
            invoiceStartDate,
            invoiceEndDate,
            status,
            isDraft,
            billingFrequencyType,
            invoiceType,
            sortBy,
          });
        })
      );
  }

  getInvoiceRunById(id: string, continuePolling: boolean = false, interval: number = 5000) {
    const sub = defer(() => this.api.corporateaccountInvoiceRunIdGet(id))
      .pipe(
        map(data => data.data.data),
        pollUntil(interval, continuePolling)
      )
      .subscribe(data =>
        applyTransaction(() => {
          this.store.upsert(data.id, data);
          this.store.setActive(id);
        })
      );

    return unsubscribeCallback(sub);
  }

  getInvoiceFinalRunAfterRegenerate(id: string) {
    const sub = defer(() => this.api.corporateaccountInvoiceRunIdGet(id))
      .pipe(map(data => data.data.data))
      .subscribe(data =>
        applyTransaction(() => {
          this.store.upsert(data.id, data);
          this.store.setActive(id);
        })
      );

    return unsubscribeCallback(sub);
  }

  sendInvoices(runId: string, invoiceIds: Array<string>) {
    from(this.api.corporateaccountRunIdInvoiceSendPost(runId, { invoiceIds }))
      .pipe(dispatchForm(InvoiceRunForms.SendInvoices))
      .subscribe();
  }

  downloadExport(run: InvoiceRun) {
    from(
      this.api.corporateaccountRunRunIdGet(run.id!, {
        responseType: 'blob',
      })
    )
      .pipe(
        map(x => new Blob([x.data], { type: MediaTypes.ZipFile })),
        dispatchForm(CommonForm.Export)
      )
      .subscribe(x =>
        saveAs(
          x,
          `invoices-${new Date(run.invoiceStartDate ?? '').toLocaleDateString()}-to-${new Date(
            run.invoiceEndDate ?? ''
          ).toLocaleDateString()}.zip`
        )
      );
  }

  generateExport(runId: string) {
    from(this.api.corporateaccountRunRunIdPost(runId))
      .pipe(dispatchForm(InvoiceRunForms.GenerateExport))
      .subscribe(() => {
        // Kick start polling the run to see when the export is ready
        this.store.update(runId, run => ({ ...run, generatingExports: true }));
      });
  }

  private updatePaginationState(pagination: PaginationState) {
    this.store.update(() => ({
      pagination,
    }));
  }

  private updateUI(ui: GetInvoiceRunRequest) {
    this.store.update(() => ({ ui }));
  }
}

export const invoiceRunService = new InvoiceRunService(new CorporateAccountApi(), invoiceRunStore);
